import { Icon } from "./Icon";

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    pageSizeOptions: number[];
    // Rows matching the filter: the paging read's `count`.
    count: number;
    // Rows before any filter. Absent until the API sends it; the total then
    // reads "{count} results" instead of "{count} of {allCount}".
    allCount?: number;
    maxVisible?: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
}

const countFormatter = new Intl.NumberFormat("en-US");

export const Pagination = ({
    currentPage,
    totalPages,
    pageSize,
    pageSizeOptions,
    count,
    allCount,
    maxVisible = 12,
    onPageChange,
    onPageSizeChange,
    hasPreviousPage,
    hasNextPage,
}: PaginationProps) => {
    const getPageNumbers = () => {
        const pages: (number | string)[] = [];

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
            return pages;
        }

        const middlePages = maxVisible - 4;
        const sidePages = Math.floor(middlePages / 2);

        pages.push(1);

        const showLeftEllipsis = currentPage > sidePages + 2;
        const showRightEllipsis = currentPage < totalPages - sidePages - 1;

        if (showLeftEllipsis) pages.push("...");

        let start = Math.max(2, currentPage - sidePages);
        let end = Math.min(totalPages - 1, currentPage + sidePages);
        if (!showLeftEllipsis) {
            end = Math.min(totalPages - 1, maxVisible - 2);
        }
        if (!showRightEllipsis) {
            start = Math.max(2, totalPages - maxVisible + 2);
        }
        for (let i = start; i <= end; i++) pages.push(i);

        if (showRightEllipsis) pages.push("...");
        pages.push(totalPages);

        return pages;
    };

    const total =
        allCount === undefined
            ? `${countFormatter.format(count)} ${count === 1 ? "result" : "results"}`
            : `${countFormatter.format(count)} of ${countFormatter.format(allCount)}`;

    // A size that is not on offer (an old ?pageSize=20) is still the size the
    // rows were read at, so it is shown as its own option rather than letting
    // the select fall back to the first one.
    const sizeOptions = pageSizeOptions.includes(pageSize)
        ? pageSizeOptions
        : [...pageSizeOptions, pageSize].sort((a, b) => a - b);

    // Mockup layout (X4): pages left, page size centre, total right. Three
    // equal columns keep the selector centred when a single page has no
    // page buttons.
    return (
        <div className="grid grid-cols-3 items-center gap-4 py-4 text-neutral">
            <div className="justify-self-start min-w-0">
                {totalPages > 1 && (
                    <nav aria-label="Pagination">
                        <div className="join border border-base-200 bg-primary/10 shadow-sm rounded-lg">
                            <button
                                type="button"
                                aria-label="Previous page"
                                className="join-item btn btn-sm btn-ghost text-neutral"
                                disabled={!hasPreviousPage}
                                onClick={() => onPageChange(currentPage - 1)}
                            >
                                <Icon name="chevron_left" className="text-lg" />
                            </button>
                            {getPageNumbers().map((page, index) =>
                                page === "..." ? (
                                    <span
                                        key={`ellipsis-${index}`}
                                        aria-hidden="true"
                                        className="join-item btn btn-sm btn-ghost no-animation pointer-events-none text-neutral/40"
                                    >
                                        …
                                    </span>
                                ) : (
                                    <button
                                        key={page}
                                        type="button"
                                        aria-label={`Go to page ${page}`}
                                        aria-current={
                                            currentPage === page ? "page" : undefined
                                        }
                                        className={`join-item btn btn-sm font-bold ${
                                            currentPage === page
                                                ? "btn-secondary"
                                                : "btn-ghost text-neutral hover:text-neutral"
                                        }`}
                                        onClick={() => onPageChange(page as number)}
                                    >
                                        {page}
                                    </button>
                                ),
                            )}
                            <button
                                type="button"
                                aria-label="Next page"
                                className="join-item btn btn-sm btn-ghost text-neutral"
                                disabled={!hasNextPage}
                                onClick={() => onPageChange(currentPage + 1)}
                            >
                                <Icon name="chevron_right" className="text-lg" />
                            </button>
                        </div>
                    </nav>
                )}
            </div>

            <label className="justify-self-center flex items-center gap-2">
                <span>Show</span>
                <select
                    name="pageSize"
                    className="select select-primary select-sm w-auto border-primary/25 text-neutral"
                    value={pageSize}
                    onChange={(e) => onPageSizeChange(Number(e.target.value))}
                >
                    {sizeOptions.map((size) => (
                        <option key={size} value={size}>
                            {size}
                        </option>
                    ))}
                </select>
                <span>per page</span>
            </label>

            <p className="justify-self-end font-bold" aria-live="polite">
                {total}
            </p>
        </div>
    );
};
